import { ChevronLeft, Menu as MenuIcon } from '@mui/icons-material';
import { Alert, AppBar, IconButton, Toolbar, Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { FC, useContext } from 'react';

import { UserContext } from '../../context';
import { environmentLabels, getEnvironment, isVendorPartner } from '../../helpers';
import { Link } from '../link';
import { Logo, FullLogoWidth, FullLogoHeight } from './';
import { UserMenu } from './UserMenu';
import clsx from 'clsx';

interface IHeaderProps {
  setShowNav(showNav: boolean): void;
  showNav: boolean;
  height: number;
  isMobile: boolean;
  showWarning: boolean;
  warningHeight: number;
  sideNavWidth: number;
  showSideNav: boolean;
  forceMobileNav?: boolean;
  showHelp?: boolean;
  showUserMenu?: boolean;
  showNews?: boolean;
  showSettings?: boolean;
}

export const Header: FC<IHeaderProps> = ({
  setShowNav,
  showNav,
  height,
  isMobile,
  showWarning,
  warningHeight,
  sideNavWidth,
  showSideNav,
  forceMobileNav = false,
  showHelp = true,
  showUserMenu = true,
  showNews = true,
  showSettings = true
}) => {
  const { user, isLoading } = useContext(UserContext);

  const classes = useStyles({
    sideNavWidth,
    showSideNav,
    forceMobileNav,
    isMobile,
    showWarning,
    FullLogoWidth,
    FullLogoHeight
  });

  const getHomeLink = () => {
    if (user && !isLoading) {
      if (isVendorPartner(user)) {
        return '/vendors';
      }
      return `/`;
    }
    return `/`;
  };

  const environmentLabel = environmentLabels[getEnvironment()];

  return (
    <AppBar
      color="inherit"
      position={'fixed'}
      className={clsx(classes.appBar, 'print--none')}
      classes={{ root: classes.appBarRoot }}
      sx={{
        height:
          (isMobile || !showSideNav) && showWarning ? `${height + warningHeight}px` : `${height}px`,
        zIndex: theme => theme.zIndex.drawer + 1
      }}
    >
      {showWarning && (
        <Box sx={{ height: warningHeight }}>
          <Alert className={classes.truncate} severity="error">
            Warning: You are on at BuilderLinq{' '}
            <span className={classes.envName}>{environmentLabel}</span> right now!
          </Alert>
        </Box>
      )}
      <Box padding="0 1rem">
        <Toolbar classes={{ root: classes.toolbarRoot }} disableGutters sx={{ height }}>
          {/* MENU BUTTON */}
          {isMobile && showSideNav && (
            <IconButton
              onClick={() => setShowNav(!showNav)}
              color="inherit"
              className={classes.menuButton}
            >
              {showNav ? (
                <ChevronLeft className={classes.iconBlack} />
              ) : (
                <MenuIcon className={classes.iconBlack} />
              )}
            </IconButton>
          )}

          {/* LOGO */}
          {(isMobile || !showSideNav) && (
            <Link to={getHomeLink()} className={classes.logoLink}>
              <Logo className={classes.logo} />
            </Link>
          )}
          {showUserMenu && (
            <UserMenu
              showNews={showNews}
              showSettings={showSettings}
              showHelp={showHelp}
              user={user}
              isMobile={isMobile}
            />
          )}
        </Toolbar>
      </Box>
    </AppBar>
  );
};

const useStyles = makeStyles<
  Theme,
  {
    sideNavWidth: number;
    showSideNav: boolean;
    forceMobileNav: boolean;
    isMobile: boolean;
    showWarning: boolean;
    FullLogoWidth: number;
    FullLogoHeight: number;
  }
>((theme: Theme) => {
  return {
    logoLink: {
      display: 'flex',
      alignItems: 'center'
    },
    logo: {
      width: ({ FullLogoWidth }) => FullLogoWidth,
      height: ({ FullLogoHeight }) => FullLogoHeight
    },
    menuButton: {
      marginRight: '1rem'
    },
    iconWhite: {
      fill: theme.palette.common.white
    },
    iconBlack: {
      fill: theme.palette.common.black
    },
    buttonText: {
      color: theme.palette.common.white,
      fontSize: '14px',
      fontWeight: 'normal',
      textTransform: 'none',
      whiteSpace: 'nowrap'
    },
    accountText: {
      display: 'none',
      [theme.breakpoints.up('md')]: {
        display: 'inline'
      }
    },
    accountIcon: {
      height: '31px',
      width: '31px',
      marginRight: theme.spacing(0.5)
    },
    truncate: {
      width: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    appBar: ({ forceMobileNav }) => {
      if (!forceMobileNav)
        return {
          [theme.breakpoints.up('md')]: {
            width: ({ sideNavWidth }) => `calc(100% - ${sideNavWidth}px)`,
            marginLeft: ({ sideNavWidth }) => sideNavWidth
          }
        };
    },
    appBarRoot: {
      boxShadow:
        '0px 2px 4px -1px rgb(0 0 0 / 20%),0px 4px 5px 0px rgba(0,0,0,0.14%), 0px 0px 0px 0px rgb(0 0 0 / 0%)'
    },
    toolbarRoot: ({ forceMobileNav }) => {
      if (!forceMobileNav) {
        return {
          height: 100,
          [theme.breakpoints.up('md')]: {
            minHeight: ({ showSideNav, isMobile, showWarning }) =>
              !isMobile && showWarning && showSideNav ? '52px' : 100,
            height: ({ showSideNav, isMobile, showWarning }) =>
              !isMobile && showWarning && showSideNav ? '52px' : 100
          }
        };
      } else {
        return { height: 100 };
      }
    },
    envName: {
      textTransform: 'capitalize'
    },
    offlineAlert: {
      width: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      backgroundColor: '#ed9e00',
      '& .MuiAlert-icon': {
        color: '#ffffff'
      }
    }
  };
});
